import { Contract, BigNumber } from "ethers";
import { useWallet } from "hooks";
import { useMultiCallContract } from "hooks/contract/multicall/useMultiCallContract";
import { useLeverageContract } from "hooks/contract/useLeverageContract";
import { get, groupBy, map, omit } from "lodash";
import { useQuery } from "react-query";
import { MainNetworkSupported, ZERO_ADDRESS } from "utils/network";
import { ERC20TokenABI } from "utils/ethereum/abi";
import { getUniswapV2Factory } from "utils/ethereum/contracts";
import { formatUnits, numberToString } from "utils/number";
import { generateMulticallRequest } from "utils/contract/multicall";
import { TokenType } from "types/token";
import { isWrapNative, wrapNativeToken } from "utils/token";
import {
  PITContractMethod,
  PriceContractMethod,
  ERC20TokenMethod,
  UniswapV2FactoryMethod,
  MulticallMethod,
} from "constants/methodName";
import { ContractName } from "constants/contractName";
import { usePITContractRead } from "hooks/contract/usePITContract";
import { usePriceContract } from "hooks/contract/core/usePriceContract";
import { useMemo } from "react";
import { useDataToUpdatePrice } from "hooks/pyth/useDataToUpdatePrice";
import {
  getLvrFromResult,
  handleGetDepositTokens,
  handleGetPriceInUsd,
} from "./helper/getDataContract";

const localChainId = localStorage.getItem("chainId");

const getRequestMulticallZksync = (
  availableBorrowTokens,
  listToken,
  account,
  provider,
  PriceContract,
  PITContract,
  PITLeverageContract,
  MultiCallContract,
  UniswapV2FactoryInfo,
  chainId,
  pythData
) => {
  /* A list of requests to token contract. */
  const listRequestToTokenContracts = [];

  /* A list of requests to Price contract. */
  const listRequestToPriceContract = [];

  /* A list of requests to PIT contract. */
  const listRequestToPITContract = [];

  /* A list of requests to PIT Leverage contract. */
  const listRequestToPITLeverageContract = [];

  /* A list of requests to Uniswapv2Factory contract */
  const listRequestToUniswapV2FactoryContract = [];

  const UniswapV2FactoryContract = new Contract(
    UniswapV2FactoryInfo.address,
    UniswapV2FactoryInfo.abi,
    provider
  );

  for (let idx = 0; idx < availableBorrowTokens.length; idx += 1) {
    const token = availableBorrowTokens[idx];
    const erc20Contract = new Contract(token.address, ERC20TokenABI);

    listRequestToTokenContracts.push(
      generateMulticallRequest(
        erc20Contract,
        ERC20TokenMethod.allowance,
        [account, PITContract.address],
        ERC20TokenMethod.allowance,
        erc20Contract.address
      ),
      generateMulticallRequest(
        erc20Contract,
        ERC20TokenMethod.balanceOf,
        [account],
        ERC20TokenMethod.balanceOf,
        erc20Contract.address
      ),
      generateMulticallRequest(
        erc20Contract,
        ERC20TokenMethod.decimals,
        [],
        ERC20TokenMethod.decimals,
        erc20Contract.address
      )
    );
    listRequestToPriceContract.push(
      generateMulticallRequest(
        PriceContract,
        PriceContractMethod.getUpdatedPriceMethod,
        [token.address, pythData.priceIds, pythData.updateData],
        PriceContractMethod.getUpdatedPriceMethod,
        token.address,
        pythData.payableAmount
      )
    );
  }

  for (let idx = 0; idx < listToken.length; idx += 1) {
    const token = listToken[idx];
    const TokenContract = new Contract(token.address, ERC20TokenABI);

    listRequestToPITContract.push(
      generateMulticallRequest(
        PITContract,
        PITContractMethod.projectTokenInfo,
        [token.address],
        token.address,
        ContractName.PITContract
      ),
      generateMulticallRequest(
        PITContract,
        PITContractMethod.pitRemainingWithUpdatePrices,
        [account, token.address, ZERO_ADDRESS, pythData.priceIds, pythData.updateData],
        token.address,
        ContractName.PITContract,
        pythData.payableAmount
      )
    );

    listRequestToPriceContract.push(
      generateMulticallRequest(
        PriceContract,
        PriceContractMethod.getUpdatedPriceMethod,
        [token.address, pythData.priceIds, pythData.updateData],
        token.address,
        ContractName.PriceContract,
        pythData.payableAmount
      )
    );

    listRequestToTokenContracts.push(
      generateMulticallRequest(
        TokenContract,
        ERC20TokenMethod.allowance,
        [account, PITContract.address],
        ERC20TokenMethod.allowance,
        token.address
      ),
      generateMulticallRequest(
        TokenContract,
        ERC20TokenMethod.balanceOf,
        [account],
        ERC20TokenMethod.balanceOf,
        token.address
      ),
      generateMulticallRequest(
        TokenContract,
        ERC20TokenMethod.decimals,
        [],
        ERC20TokenMethod.decimals,
        token.address
      )
    );

    listRequestToPITLeverageContract.push(
      generateMulticallRequest(
        PITLeverageContract,
        PITContractMethod.isLeveragePosition,
        [account, token.address],
        PITContractMethod.isLeveragePosition,
        ContractName.PITLeverageContract
      ),
      generateMulticallRequest(
        PITLeverageContract,
        PITContractMethod.getLeverageType,
        [account, token.address],
        PITContractMethod.getLeverageType,
        ContractName.PITLeverageContract
      )
    );
  }

  if (!MainNetworkSupported.includes(+chainId)) {
    listToken.forEach((collateralToken) => {
      availableBorrowTokens.forEach((lendingToken) => {
        listRequestToUniswapV2FactoryContract.push(
          generateMulticallRequest(
            UniswapV2FactoryContract,
            UniswapV2FactoryMethod.getPair,
            [collateralToken.address, lendingToken.address],
            collateralToken.address,
            ContractName.UniswapV2FactoryContract
          )
        );
      });
    });
  }

  return [
    generateMulticallRequest(
      MultiCallContract,
      MulticallMethod.getEthBalance,
      [account],
      MultiCallContract.address,
      ContractName.MultiCallContract
    ),
    ...listRequestToPITLeverageContract,
    ...listRequestToTokenContracts,
    ...listRequestToPriceContract,
    ...listRequestToPITContract,
    ...listRequestToUniswapV2FactoryContract,
  ];
};

const useAvailableMultiCall = ({ collaterals = [], availableBorrowTokens }) => {
  const { chainId, account, provider } = useWallet();

  const { callRequest, multiCallSMC } = useMultiCallContract();

  const isMainNet = MainNetworkSupported.includes(+chainId);

  const { PriceContract } = usePriceContract();
  const { leverageContract } = useLeverageContract();
  const {
    data: { PitContract },
  } = usePITContractRead();

  const tokenAddressList = useMemo(() => collaterals.map((t) => t.address), [collaterals]);
  const { refetch } = useDataToUpdatePrice(tokenAddressList);

  return useQuery(
    ["available-multicall", account, tokenAddressList.sort().toString(), chainId],
    async () => {
      const { data: pythData } = await refetch();
      const listToken = [...collaterals];

      const UniswapV2FactoryContract = getUniswapV2Factory(chainId ?? localChainId);

      const requests = getRequestMulticallZksync(
        availableBorrowTokens,
        listToken,
        account,
        provider,
        PriceContract,
        PitContract,
        leverageContract,
        multiCallSMC,
        UniswapV2FactoryContract,
        chainId,
        pythData
      );

      const results = {
        UniswapV2FactoryContract: [],
        ...(await callRequest(requests)),
      };

      const checkPositionRes = results[ContractName.PITLeverageContract]
        .filter((call) => call.methodName === PITContractMethod.isLeveragePosition)
        .map((call) => call.returnValues[0]);
      const leverageTypeRes = results[ContractName.PITLeverageContract]
        .filter((call) => call.methodName === PITContractMethod.getLeverageType)
        .map((call) => call.returnValues[0]);

      const leveragePositionMap = new Map();
      listToken.forEach((token, index) => {
        leveragePositionMap.set(token.address, {
          isLeverage: checkPositionRes[index],
          leverageType: leverageTypeRes[index],
        });
      });

      const { name: nativeName, symbol: nativeSymbol, logo: nativeLogo } = wrapNativeToken(chainId);
      const nativeBalanceValue = get(
        results,
        [ContractName.MultiCallContract, 0, "returnValues"],
        BigNumber.from(0)
      );
      const nativeBalance = numberToString(nativeBalanceValue / 10 ** 18);
      let userTokenInfo = [...availableBorrowTokens, ...listToken].map((token) => {
        const tokenContract = get(results, [token.address], []);

        const methodData = groupBy(tokenContract, "methodName");

        const balanceOf = get(methodData, [ERC20TokenMethod.balanceOf, 0, "returnValues", 0], "0");
        const decimal = get(methodData, [ERC20TokenMethod.decimals, 0, "returnValues", 0], "0");

        const isNative = isWrapNative(token.address, chainId);
        return {
          price: 0,
          ...token,
          allowance: !!get(methodData, [ERC20TokenMethod.allowance, 0, "returnValues"], false),
          balanceOf: isNative ? nativeBalance : formatUnits(balanceOf, decimal),
          name: isNative ? nativeName : token.name,
          symbol: isNative ? nativeSymbol : token.symbol,
          logo: isNative ? nativeLogo : token.logo,
          decimalNumber: formatUnits(decimal, 0),
        };
      });

      const availableDepositTokens = handleGetDepositTokens({
        results: { ...results },
        listToken,
        isMainNet,
      }).map((o) => {
        const isNative = isWrapNative(o.address, chainId);
        return {
          ...o,
          balance: isNative ? nativeBalance : o.balance,
          balanceInUsd: isNative ? nativeBalance * o.price : o.balanceInUsd,
          name: isNative ? nativeName : o.name,
          symbol: isNative ? nativeSymbol : o.symbol,
          logo: isNative ? nativeLogo : o.logo,
          isLeverage: leveragePositionMap?.get(o.address)?.isLeverage,
          leverageType: leveragePositionMap?.get(o.address)?.leverageType,
        };
      });

      const lvrByProjectTokens = listToken.reduce(
        (res, o) => ({
          ...res,
          [o.address]: getLvrFromResult(results, o.address),
        }),
        {}
      );

      const priceOfTokens = [...listToken, ...availableBorrowTokens].reduce((res, o) => {
        const { priceCollateralTokenBN, priceLendingTokenBN, priceDecimal } = handleGetPriceInUsd(
          results,
          o.address
        );

        return {
          ...res,
          [o.address]: {
            [TokenType.COLLATERAL]: formatUnits(priceCollateralTokenBN, priceDecimal),
            [TokenType.LENDING]: formatUnits(priceLendingTokenBN, priceDecimal),
          },
        };
      }, {});

      // inject price to userTokenInfo
      userTokenInfo = userTokenInfo.map((x) => ({
        ...x,
        price: priceOfTokens[x.address] || 0,
      }));

      const decimalOfContractToken = {};
      map(
        omit(results, [
          ContractName.PriceContract,
          isMainNet && ContractName.UniswapV2FactoryContract,
        ]),
        (obj) => {
          const key = get(obj, [0, "contract", "address"]).toLowerCase();

          const value = get(
            groupBy(obj, "methodName"),
            [ERC20TokenMethod.decimals, 0, "returnValues", 0],
            0
          );

          decimalOfContractToken[key] = +value;
        }
      );

      return {
        availableDepositTokens,
        userTokenInfo,
        decimalOfContractToken,
        lvrByProjectTokens,
        priceOfTokens,
      };
    },
    {
      retry: 3,
      enabled:
        !!PitContract &&
        !!PriceContract &&
        !!account &&
        !!collaterals?.length &&
        !!availableBorrowTokens?.length &&
        !!chainId,
      placeholderData: {
        availableDepositTokens: availableBorrowTokens,
        userTokenInfo: collaterals,
        decimalOfContractToken: {},
        lvrByProjectTokens: {},
        priceOfTokens: {},
      },
    }
  );
};

export default useAvailableMultiCall;
